import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { get, post } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import MembershipFilled from "atoms/icon/icons/ic_memberships_xsmall_filled_ol.svg";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import Star from "atoms/icon/icons/star.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { TermsAndCondition } from "atoms/terms-and-condition/TermsAndCondition";
import { AnimatedBackground } from "components/AnimatedBackground/AnimatedBackground";
import useDunningMembership from "hooks/useDunningMemberships";
import useHistory from "hooks/useHistory";
import MonthlyRewardsOrTreatments from "modules/memberships/MonthlyRewardsOrTreatments/MonthlyRewardsOrTreatments";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import { InfoBlock } from "molecules/info-blocks/InfoBlock";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import {
  getPDPBannerDiscountText,
  getProductTitle,
} from "services/HelperService";
import { getDiscountCustomColor } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import aOrAn from "utils/aOrAn";
import {
  beautyBankText,
  formatBillingPeriodInterval,
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import { FormattedString } from "utils/FormattedStrings";
import pluralize from "utils/pluralize";

const load = async (membershipId: string, source?: string) => {
  const membershipListing = queryClient.fetchQuery(
    ["/membership", { membershipId }],
    () => get("/memberships/{membershipId}", { membershipId })
  );

  const membershipRewards = queryClient.fetchQuery(
    ["/membership/{membershipId}/rewards", { membershipId }],
    () => get("/memberships/{membershipId}/rewards", { membershipId })
  );

  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );

  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );

  return await waitObj({
    source,
    membershipListing,
    membershipRewards,
    userMemberships,
    user,
    shopDiscounts,
    customShopCategories,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const membershipPDPLoader: LoaderFunction = ({ params }) => {
  const membershipId = params.id;
  const source =
    params.source == "Package"
      ? "package"
      : params.source == "Treatment"
      ? "treatment"
      : undefined;

  if (!membershipId) return redirect("..");
  return load(membershipId, source);
};

export const addMembershipToCart = async (membershipId: string) => {
  try {
    const response = await post("/orders/current/lineitems", {
      membershipId: membershipId,
    });
    queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
    return response;
  } catch (error) {
    return {
      error: "Not able to add order to cart. try again later",
      details: error,
    };
  }
};

// Extracted Components
type StickySignUpBonusCardProps = {
  signUpBonus: Models<"MembershipRewardGroupModel">;
  dunningStatus?: boolean;
  animateStickyCard: boolean;
  onOpenModal: () => void;
};

const StickySignUpBonusCard = ({
  signUpBonus,
  dunningStatus,
  animateStickyCard,
  onOpenModal,
}: StickySignUpBonusCardProps) => {
  return (
    <div
      className={cx(
        "pointer-events-none sticky z-30 bg-brand-color transition-opacity duration-1000 ease-in-out",
        dunningStatus
          ? "top-[calc(96px_+_env(safe-area-inset-top))]"
          : "top-[calc(54px_+_env(safe-area-inset-top))]",
        animateStickyCard ? "opacity-100" : "opacity-0"
      )}
    >
      <div className="px-4">
        <button
          onClick={onOpenModal}
          className="pointer-events-auto flex w-full items-center justify-between py-3 transition-colors hover:opacity-90"
        >
          <div className="min-w-0 flex-1 text-left">
            <div className="text-bold3 text-white">
              Don&apos;t miss out on your sign up bonus!
            </div>
            <div className="text-body3 text-white/90">
              {signUpBonus.membershipRewards.length === 1
                ? (() => {
                    const item = signUpBonus.membershipRewards[0];
                    const itemName = getProductTitle(
                      item.packageListingTitle,
                      item.packageName
                    );
                    return item.quantity === 1
                      ? `Receive ${aOrAn(itemName)} when you join`
                      : `Receive ${item.quantity} ${itemName} when you join`;
                  })()
                : "Receive a bonus when you join today."}
            </div>
          </div>
          <Icon
            svg={ChevronRight}
            size="small"
            color="white"
            className="ml-3 flex-shrink-0"
          />
        </button>
      </div>
    </div>
  );
};

type MembershipHeaderProps = {
  imageUrl?: string | null;
  name: string;
};

const MembershipHeader = ({ imageUrl, name }: MembershipHeaderProps) => {
  if (imageUrl) {
    return (
      <div className="relative w-full">
        <div
          className="relative aspect-[16/9] w-full overflow-hidden bg-cover bg-center"
          style={{
            backgroundImage: `url(${imageUrl})`,
          }}
        />
        <div className="absolute inset-x-0 bottom-0 flex items-end bg-gradient-to-t from-white via-white/80 to-transparent pt-12">
          <div className="w-full px-4 pb-3 text-center font-serif text-h2 text-primary">
            {name}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 pb-4 pt-6">
      <div className="text-center font-serif text-h2 text-primary">{name}</div>
    </div>
  );
};

type SignUpBonusCardProps = {
  signUpBonus: Models<"MembershipRewardGroupModel">;
  membershipName: string;
  cardRef: React.RefObject<HTMLDivElement>;
  onOpenModal: () => void;
};

const SignUpBonusCard = ({
  signUpBonus,
  membershipName,
  cardRef,
  onOpenModal,
}: SignUpBonusCardProps) => {
  return (
    <div ref={cardRef} className="px-4 py-6">
      <div
        className="relative rounded-xl"
        style={{
          animation: "floatingShadow 3s ease-in-out infinite",
        }}
      >
        <div className="relative flex items-center gap-3 rounded-xl bg-brand-tint p-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
            <Icon svg={MembershipFilled} size="xsmall" color="brandColor" />
          </div>
          <div className="flex flex-1 flex-col">
            <div className="text-h4 font-serif font-semibold text-primary">
              Sign Up Bonus!
            </div>
            <div className="pt-1 text-body2 text-secondary">
              {signUpBonus.membershipRewards.length === 1
                ? (() => {
                    const item = signUpBonus.membershipRewards[0];
                    const itemName = getProductTitle(
                      item.packageListingTitle,
                      item.packageName
                    );
                    return item.quantity === 1
                      ? `Receive ${aOrAn(itemName)} when you join`
                      : `Receive ${item.quantity} ${itemName} when you join`;
                  })()
                : `Receive a one-time sign-up bonus when you join as ${aOrAn(
                    membershipName
                  )} member`}
            </div>
            <button
              onClick={onOpenModal}
              className="pt-2 text-left text-bold2 text-brand-color"
            >
              More details →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type BeautyBankCardProps = {
  creditAmount: string;
  descriptionText: string;
};

const BeautyBankCard = ({
  creditAmount,
  descriptionText,
}: BeautyBankCardProps) => {
  return (
    <div className="px-4 pb-6">
      <div className="relative aspect-[1.7/1] overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-black to-gray-800 shadow-lg">
        {/* Animated background */}
        <AnimatedBackground
          mode="black"
          durationSeconds={10}
          className="absolute inset-0 h-full w-full opacity-50"
        />

        {/* Subtle card pattern/texture */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Scattered plus signs */}
        <div className="absolute right-16 top-6 text-4xl font-light text-white opacity-20">
          +
        </div>
        <div className="absolute right-6 top-20 text-3xl font-light text-white opacity-15">
          +
        </div>
        <div className="absolute left-8 top-12 text-3xl font-light text-white opacity-15">
          +
        </div>
        <div className="absolute bottom-12 left-20 text-5xl font-light text-white opacity-20">
          +
        </div>
        <div className="absolute bottom-20 right-24 text-3xl font-light text-white opacity-15">
          +
        </div>
        <div className="absolute bottom-8 right-10 text-4xl font-light text-white opacity-20">
          +
        </div>

        <div className="relative flex h-full flex-col justify-between p-6">
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-white/60">
              BANK EVERY MONTH
            </div>
            <div className="flex items-center gap-3 py-2">
              <Icon svg={RepeatCash} size="xlarge" color="white" />
              <div className="font-serif text-h1 font-semibold text-white">
                {creditAmount}
              </div>
            </div>
          </div>

          <div>
            <div className="text-body2 leading-relaxed text-white">
              {descriptionText}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type MilestoneBonusCardsProps = {
  milestoneBonuses: Models<"MembershipRewardGroupModel">[];
  membershipName: string;
  onOpenModal: (bonus: Models<"MembershipRewardGroupModel">) => void;
  calculatePeriodCount: (
    rewardGroup: Models<"MembershipRewardGroupModel">
  ) => number;
  calculatePeriodLabel: (
    rewardGroup: Models<"MembershipRewardGroupModel">
  ) => string;
};

const MilestoneBonusCards = ({
  milestoneBonuses,
  membershipName,
  onOpenModal,
  calculatePeriodCount,
  calculatePeriodLabel,
}: MilestoneBonusCardsProps) => {
  return (
    <div className="bg-white">
      <div className="px-4 py-6">
        <div className="flex flex-col gap-3">
          {milestoneBonuses.slice(0, 1).map((bonus, index) => {
            const periodCount = calculatePeriodCount(bonus);
            const periodLabel = calculatePeriodLabel(bonus);

            return (
              <div
                key={bonus.id}
                className="relative rounded-xl"
                style={{
                  boxShadow:
                    "0 4px 12px rgb(var(--repeatmd-brand-color) / 0.15), 0 2px 6px rgb(var(--repeatmd-brand-color) / 0.1)",
                }}
              >
                <div className="relative flex items-center gap-3 rounded-xl bg-brand-tint p-4">
                  {index === 0 && (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        svg={MembershipFilled}
                        size="xsmall"
                        color="brandColor"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col">
                    <div className="text-h4 font-serif font-semibold text-primary">
                      {periodCount}-{periodLabel} member bonus!
                    </div>
                    {index === 0 && (
                      <div className="pt-1 text-body2 text-secondary">
                        {!bonus.hasSelectableRewards &&
                        bonus.membershipRewards.length === 1 ? (
                          (() => {
                            const item = bonus.membershipRewards[0];
                            const itemName = getProductTitle(
                              item.packageListingTitle,
                              item.packageName
                            );
                            return item.quantity === 1 ? (
                              <>
                                Receive {aOrAn(itemName)} after {periodCount}{" "}
                                {periodLabel} as {aOrAn(membershipName)} member
                              </>
                            ) : (
                              <>
                                Receive <strong>{item.quantity}</strong>{" "}
                                {item.quantity === 1
                                  ? itemName
                                  : `${itemName} treatments`}{" "}
                                after {periodCount} {periodLabel} as{" "}
                                {aOrAn(membershipName)} member
                              </>
                            );
                          })()
                        ) : (
                          <>
                            Receive a one-time bonus after {periodCount}{" "}
                            {periodLabel} as {aOrAn(membershipName)} member
                          </>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => onOpenModal(bonus)}
                      className="pt-2 text-left text-bold2 text-brand-color"
                    >
                      More details →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MembershipPDP = () => {
  const {
    membershipListing,
    membershipRewards,
    source,
    userMemberships,
    user,
    shopDiscounts,
    customShopCategories,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const { routeBackNumber } = useHistory();
  const { dunningMembership } = useDunningMembership();
  const flagBeautyBank = useFeatureFlag("membershipBeautyBank");
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const isV3Enabled = true;

  const currentMembership = userMemberships.find((membership) => {
    const membershipIdMatches =
      membership.membership.id == membershipListing.id ||
      membership.membership.baseMembershipId == membershipListing.id;

    const baseMembershipIdMatches =
      membershipListing.baseMembershipId != null &&
      (membership.membership.baseMembershipId ==
        membershipListing.baseMembershipId ||
        membership.membership.id == membershipListing.baseMembershipId);

    return (
      (membershipIdMatches || baseMembershipIdMatches) && !membership.canceled
    );
  });

  const discountData = shopDiscounts?.membershipDiscounts[membershipListing.id];
  const discountText =
    discountData && getPDPBannerDiscountText(discountData, "Membership");
  const discountColor = getDiscountCustomColor(
    discountData,
    customShopCategories
  );

  const parsedBillingPeriodInterval = parseBillingPeriodName(
    membershipListing.billingPeriodInterval
  );
  const baseBillingPeriodInterval = getBillingIntervalBase(
    parsedBillingPeriodInterval ?? null
  );
  const capitalizedBillingPeriodInterval =
    baseBillingPeriodInterval.charAt(0).toUpperCase() +
    baseBillingPeriodInterval.slice(1);

  const [milestoneModalOpen, setMilestoneModalOpen] = useState<boolean>(false);
  const [milestoneModalContent, setMilestoneModalContent] =
    useState<Models<"MembershipRewardGroupModel">>();
  const [signUpBonusModalOpen, setSignUpBonusModalOpen] =
    useState<boolean>(false);
  const [showStickyCard, setShowStickyCard] = useState<boolean>(false);
  const [renderStickyCard, setRenderStickyCard] = useState<boolean>(false);
  const [animateStickyCard, setAnimateStickyCard] = useState<boolean>(false);
  const signUpBonusCardRef = useRef<HTMLDivElement>(null);

  const addMembershipMutation = useMutation({
    mutationFn: (membershipId: string) => addMembershipToCart(membershipId),
    onSuccess: async () => {
      await invalidateCurrentOrderDetails();
      navigate("/cart");
    },
  });

  const signUpBonus = membershipRewards.find((group) => {
    if (isFlexibleMembershipsEnabled) {
      return (
        group.billingPeriodsBeforeFirstReward === 0 &&
        group.billingPeriodInterval === 0
      );
    }
    return group.monthsBeforeFirstReward === 0 && group.periodMonths === 0;
  });

  const beautyBankCreditAmount =
    membershipListing.membershipRewardGroups?.find(
      (item) =>
        item.beautyBank?.creditAmount !== null &&
        item.membershipRewards.length === 0
    )?.beautyBank?.creditAmount ?? 0;

  const showBeautyBankCard = flagBeautyBank && !!beautyBankCreditAmount;
  const formattedBeautyBankCreditAmount = formatCurrency(
    beautyBankCreditAmount,
    0
  );

  // Track scroll position to show/hide sticky card
  useEffect(() => {
    if (!signUpBonus) return;

    const handleScroll = () => {
      if (signUpBonusCardRef.current) {
        const rect = signUpBonusCardRef.current.getBoundingClientRect();
        // Show sticky card when original card scrolls past the top of viewport
        setShowStickyCard(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [signUpBonus]);

  // Control animation timing for sticky card
  useEffect(() => {
    if (showStickyCard && signUpBonus) {
      // Wait 2 seconds before rendering the card
      const renderTimer = setTimeout(() => {
        setRenderStickyCard(true);
        // Then wait a frame for the DOM to mount before animating
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimateStickyCard(true);
          });
        });
      }, 2000);
      return () => clearTimeout(renderTimer);
    } else {
      // Immediate disappearance - reset immediately
      setRenderStickyCard(false);
      setAnimateStickyCard(false);
    }
  }, [showStickyCard, signUpBonus]);

  const milestoneBonuses = membershipRewards
    .filter((group) => {
      if (isFlexibleMembershipsEnabled) {
        return (
          group.billingPeriodsBeforeFirstReward > 0 &&
          group.billingPeriodInterval === 0
        );
      }
      return group.monthsBeforeFirstReward > 0 && group.periodMonths === 0;
    })
    .sort((a, b) =>
      isFlexibleMembershipsEnabled
        ? a.billingPeriodsBeforeFirstReward - b.billingPeriodsBeforeFirstReward
        : a.monthsBeforeFirstReward - b.monthsBeforeFirstReward
    );

  const buildCommitmentPeriodMessage = (
    membershipListing: Models<"MembershipModel">,
    isFlexibleMembershipsEnabled: boolean
  ) => {
    const { commitment = 0, billingPeriodIntervalCount = 1 } =
      membershipListing;

    if (isFlexibleMembershipsEnabled) {
      const totalPeriods = commitment * billingPeriodIntervalCount;
      return `${totalPeriods} ${baseBillingPeriodInterval} commitment required`;
    }

    return `${commitment} month commitment required`;
  };

  const rewardGroupBillingPeriodCalculation = (
    rewardGroup: Models<"MembershipRewardGroupModel">
  ) => {
    if (isFlexibleMembershipsEnabled) {
      return (
        rewardGroup.billingPeriodsBeforeFirstReward *
        (membershipListing.billingPeriodIntervalCount ?? 1)
      );
    }
    return rewardGroup.monthsBeforeFirstReward;
  };

  const pluralizeBillingPeriodBeforeFirstReward = (
    rewardGroup: Models<"MembershipRewardGroupModel">
  ) => {
    const count = rewardGroupBillingPeriodCalculation(rewardGroup);

    const baseInterval = baseBillingPeriodInterval ?? "period";

    const singularBaseInterval = isFlexibleMembershipsEnabled
      ? baseInterval
      : "month";
    const pluralBaseInterval = isFlexibleMembershipsEnabled
      ? `${baseInterval}s`
      : "months";

    return pluralize(count, singularBaseInterval, pluralBaseInterval);
  };

  const buildSignUpBonusModalIntro = (
    signUpBonus: Models<"MembershipRewardGroupModel">,
    membershipListing: Models<"MembershipModel">
  ) => {
    const count = signUpBonus.hasSelectableRewards
      ? signUpBonus.quantityPerPeriod
      : signUpBonus.membershipRewards.length;
    return (
      <>
        <span className="text-bold1">
          {signUpBonus.hasSelectableRewards ? "Select" : "Receive"} {count}
        </span>{" "}
        bonus {pluralize(count, "treatment", "treatments")}{" "}
        {signUpBonus.hasSelectableRewards && "from the below"} when you sign up
        as {aOrAn(membershipListing.name)} member
      </>
    );
  };

  const buildMilestoneBonusModalTitle = (
    milestoneModalContent: Models<"MembershipRewardGroupModel">,
    isFlexibleMembershipsEnabled: boolean,
    membershipBillingPeriodIntervalCount?: number
  ) => {
    if (isFlexibleMembershipsEnabled) {
      const periodCount =
        milestoneModalContent.billingPeriodsBeforeFirstReward *
        (membershipBillingPeriodIntervalCount ?? 1);
      return `${periodCount}-${capitalizedBillingPeriodInterval} member bonus!`;
    }
    return `${milestoneModalContent.monthsBeforeFirstReward}-month member bonus!`;
  };

  const buildMilestoneBonusModalIntro = (
    milestoneModalContent: Models<"MembershipRewardGroupModel">,
    membershipListing: Models<"MembershipModel">
  ) => {
    const count = milestoneModalContent.hasSelectableRewards
      ? milestoneModalContent.quantityPerPeriod
      : milestoneModalContent.membershipRewards.length;

    const periodCount = rewardGroupBillingPeriodCalculation(
      milestoneModalContent
    );

    return (
      <>
        <span className="text-bold1">
          {milestoneModalContent.hasSelectableRewards ? "Select" : "Receive"}{" "}
          {count}
        </span>{" "}
        bonus {pluralize(count, "treatment", "treatments")} after {periodCount}{" "}
        {pluralizeBillingPeriodBeforeFirstReward(milestoneModalContent)} as{" "}
        {aOrAn(membershipListing.name)} member
      </>
    );
  };

  const getGuestUserMembershipText = () => {
    if (user.isGuest) {
      return "Sign up to join";
    }
    if (currentMembership == null) {
      return "Become a member";
    }
    return "You're already a member";
  };

  const handleAddToCart = () => {
    addMembershipMutation.mutate(membershipListing.id);
  };

  const membershipPrice = () => {
    if (isFlexibleMembershipsEnabled) {
      const formattedPrice = formatBillingPeriodInterval(
        membershipListing.billingPeriodCost,
        parsedBillingPeriodInterval ?? null,
        membershipListing.billingPeriodIntervalCount ?? null
      );
      // Split the formatted price to apply bold to the amount
      const parts = formattedPrice.split("/");
      return (
        <span className="text-body1">
          <span className="text-bold1">{parts[0]}</span>
          {parts[1] && <span>/{parts[1]}</span>}
        </span>
      );
    }

    return (
      <span className="text-body1">
        <span className="text-bold1">
          {formatCurrency(membershipListing.monthlyCost)}
        </span>
        <span>/month</span>
      </span>
    );
  };

  return (
    <>
      {isV3Enabled ? (
        <div className="flex h-full min-h-screen flex-col">
          <TopNav
            startIconSVG={ChevronLeft}
            onStartIconClick={() => navigate(routeBackNumber)}
          >
            <div className="flex max-h-[32px] items-center">
              <h1 className="text-xl">Memberships</h1>
            </div>
          </TopNav>

          {/* Sticky Sign-Up Bonus Card */}
          {renderStickyCard && signUpBonus && (
            <StickySignUpBonusCard
              signUpBonus={signUpBonus}
              dunningStatus={dunningMembership.status}
              animateStickyCard={animateStickyCard}
              onOpenModal={() => setSignUpBonusModalOpen(true)}
            />
          )}

          <div className="flex flex-1 flex-col overflow-y-auto bg-white">
            {/* Image Section with Title Overlay */}
            <MembershipHeader
              imageUrl={membershipListing.imageUrl}
              name={membershipListing.name}
            />

            {/* Pricing Section */}
            <div className="px-4">
              <div className="flex flex-col items-center pb-4">
                {user.isGuest ? (
                  <div className="flex flex-col items-center text-body1">
                    <span>Sign up to see price</span>
                  </div>
                ) : (
                  <div>{membershipPrice()}</div>
                )}
              </div>
            </div>

            {discountData && !user.isGuest && (
              <div className="px-4 pt-4">
                <InfoBox
                  text={discountText}
                  type="discount"
                  leftIcon={OfferIcon}
                  customColor={discountColor ?? undefined}
                />
              </div>
            )}

            {signUpBonus && (
              <SignUpBonusCard
                signUpBonus={signUpBonus}
                membershipName={membershipListing.name}
                cardRef={signUpBonusCardRef}
                onOpenModal={() => setSignUpBonusModalOpen(true)}
              />
            )}

            <div className="bg-white">
              <div className="px-4 py-6">
                <div className="flex flex-col gap-2">
                  <div className="text-h4 font-serif font-semibold text-primary">
                    Description
                  </div>
                  <div className="mb-2">
                    <div className="break-words [&_p]:text-body2">
                      <FormattedString>
                        {membershipListing.description || ""}
                      </FormattedString>
                    </div>
                  </div>
                  {membershipListing.commitment > 1 && (
                    <div className="pt-2 text-body2 text-secondary">
                      {buildCommitmentPeriodMessage(
                        membershipListing,
                        isFlexibleMembershipsEnabled
                      )}
                    </div>
                  )}
                </div>
              </div>

              {membershipListing.membershipBenefits.length > 0 && (
                <div className="px-4 py-6">
                  <div className="mb-2 flex flex-row items-center gap-2">
                    <Icon
                      svg={Star}
                      size="x2small"
                      color="brandColor"
                      className="flex-shrink-0"
                    />
                    <span className="text-h4 font-serif font-semibold text-primary">
                      Member-Only Benefits
                    </span>
                  </div>
                  <div className="pb-2 text-body2 text-secondary">
                    Sign up today and enjoy exclusive member-only benefits
                  </div>
                  <ul className="list-outside list-disc pl-5 marker:text-brand-color">
                    {membershipListing.membershipBenefits.map((benefit) => {
                      if (benefit.description) {
                        return (
                          <li key={benefit.id}>
                            <div className="py-1 text-left text-body2 text-primary">
                              {benefit.description}
                            </div>
                          </li>
                        );
                      }
                    })}
                  </ul>
                </div>
              )}

              {showBeautyBankCard && (
                <BeautyBankCard
                  creditAmount={formattedBeautyBankCreditAmount}
                  descriptionText={beautyBankText(
                    isFlexibleMembershipsEnabled,
                    formattedBeautyBankCreditAmount,
                    baseBillingPeriodInterval,
                    membershipListing
                  )}
                />
              )}
            </div>

            <MonthlyRewardsOrTreatments
              membershipsRewards={membershipRewards}
              membershipBillingPeriodInterval={baseBillingPeriodInterval}
              membershipBillingPeriodIntervalCount={
                membershipListing.billingPeriodIntervalCount ?? 1
              }
              flagBeautyBank={flagBeautyBank}
              hasAnyImagesOnPage={membershipRewards.some((rg) =>
                rg.membershipRewards.some((mr) => !!mr.packageImageUrl)
              )}
            />

            {membershipListing.active && milestoneBonuses.length > 0 && (
              <MilestoneBonusCards
                milestoneBonuses={milestoneBonuses}
                membershipName={membershipListing.name}
                onOpenModal={(bonus) => {
                  setMilestoneModalContent(bonus);
                  setMilestoneModalOpen(true);
                }}
                calculatePeriodCount={rewardGroupBillingPeriodCalculation}
                calculatePeriodLabel={pluralizeBillingPeriodBeforeFirstReward}
              />
            )}

            <div className="px-4 pb-4 pt-4">
              <TermsAndCondition hideText />
            </div>
          </div>

          <div className="z-35 sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full">
            <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-white to-transparent" />

            <div className="relative bg-white px-4 pb-4 pt-2">
              {source ? (
                <div className="flex flex-col gap-3">
                  <Button
                    fullWidth={true}
                    onClick={async () => navigate(routeBackNumber)}
                  >
                    Back to {source}
                  </Button>
                  <Button
                    fullWidth={true}
                    style={"secondary"}
                    onClick={async () => navigate("/shop/memberships")}
                  >
                    Shop all memberships
                  </Button>
                </div>
              ) : (
                <Button
                  disabled={
                    !membershipListing.active ||
                    currentMembership != null ||
                    user.isGuest ||
                    addMembershipMutation.isLoading
                  }
                  fullWidth={true}
                  onClick={() => {
                    if (
                      user.platform?.platformSettings
                        ?.zenotiWebStoreMembershipPurchaseRequired
                    ) {
                      if (user.platformLocation?.zenotiId) {
                        window.open(
                          `${user.platform.platformSettings.zenotiWebStoreUrl}/webstoreNew/sales/membership/${user.platformLocation.zenotiId}`,
                          "_blank"
                        );
                      }
                    } else {
                      handleAddToCart();
                    }
                  }}
                >
                  {getGuestUserMembershipText()}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full min-h-screen flex-col">
          <TopNav
            startIconSVG={ChevronLeft}
            onStartIconClick={() => navigate(routeBackNumber)}
          >
            <div className="flex max-h-[32px] items-center">
              <h1 className="text-xl">Memberships</h1>
            </div>
          </TopNav>

          <div className="flex flex-1 flex-col overflow-y-auto bg-one">
            <div
              className="relative aspect-[16/9] w-full overflow-hidden bg-cover bg-center"
              style={{
                backgroundImage: `url(${membershipListing.imageUrl})`,
              }}
            />
            <div className="flex flex-col items-center">
              <div className="flex flex-col items-center gap-6 px-4 pb-4 pt-8">
                <div className="flex flex-col items-center">
                  <div className="text-center font-serif text-h1 text-primary">
                    {membershipListing.name}
                  </div>
                  <div className="flex pt-3">
                    {!user.isGuest ? (
                      membershipPrice()
                    ) : (
                      <span className="text-sub1 text-primary">
                        Sign up to see price
                      </span>
                    )}
                  </div>
                  {discountData && (
                    <div className="py-4">
                      <InfoBox
                        text={discountText}
                        type="discount"
                        customColor={discountColor ?? undefined}
                      />
                    </div>
                  )}
                  <div className="pt-5 text-center text-body1 text-secondary">
                    <FormattedString>
                      {membershipListing.description || ""}
                    </FormattedString>
                  </div>
                  {membershipListing.commitment > 1 && (
                    <div className="pt-5 text-center text-bold2 text-secondary">
                      {buildCommitmentPeriodMessage(
                        membershipListing,
                        isFlexibleMembershipsEnabled
                      )}
                    </div>
                  )}
                </div>
                {showBeautyBankCard && (
                  <InfoBlock
                    icon={RepeatCash}
                    description={
                      <span className="text-bold1">
                        {beautyBankText(
                          isFlexibleMembershipsEnabled,
                          formattedBeautyBankCreditAmount,
                          baseBillingPeriodInterval,
                          membershipListing
                        )}
                      </span>
                    }
                  />
                )}
                {signUpBonus && (
                  <>
                    {signUpBonus.membershipRewards.length === 1 ? (
                      <InfoBlock
                        icon={MembershipFilled}
                        title="JOIN NOW FOR YOUR SIGN UP BONUS!"
                        description={(() => {
                          const item = signUpBonus.membershipRewards[0];
                          const itemName = getProductTitle(
                            item.packageListingTitle,
                            item.packageName
                          );
                          return item.quantity === 1
                            ? `Receive ${aOrAn(itemName)} when you join`
                            : `Receive ${item.quantity} ${itemName} when you join`;
                        })()}
                        callToAction="More details"
                        callToActionClick={() => setSignUpBonusModalOpen(true)}
                      />
                    ) : (
                      <InfoBlock
                        icon={MembershipFilled}
                        title="JOIN NOW FOR YOUR SIGN UP BONUS!"
                        description={`Receive a one-time sign-up bonus when you join as ${aOrAn(
                          membershipListing.name
                        )} member`}
                        callToAction="More details"
                        callToActionClick={() => setSignUpBonusModalOpen(true)}
                      />
                    )}
                  </>
                )}
              </div>
              {membershipListing.membershipBenefits.length > 0 && (
                <div
                  className={cx("mb-1 rounded-t-2xl bg-white px-7 pb-8 pt-8")}
                >
                  <div className="flex items-center justify-center">
                    <Icon color="brandColor" size="xsmall" svg={Star} />
                  </div>
                  <div className="my-4 text-center text-sub1 text-brand-color">
                    ACCESS MEMBER-ONLY BENEFITS
                  </div>
                  <div className="pb-6 text-center text-body1 text-secondary">
                    Sign up today and enjoy exclusive member-only benefits
                  </div>
                  <ul className="list-outside list-disc pl-5 marker:text-[21px] marker:text-brand-color">
                    {membershipListing.membershipBenefits.map((benefit) => {
                      if (benefit.description) {
                        return (
                          <li key={benefit.id}>
                            <div className="py-2 text-left text-body1 text-primary">
                              {benefit.description}
                            </div>
                          </li>
                        );
                      }
                    })}
                  </ul>
                </div>
              )}
            </div>
            <MonthlyRewardsOrTreatments
              membershipsRewards={membershipRewards}
              membershipBillingPeriodInterval={baseBillingPeriodInterval}
              membershipBillingPeriodIntervalCount={
                membershipListing.billingPeriodIntervalCount ?? 1
              }
              flagBeautyBank={flagBeautyBank}
            />

            {membershipListing.active && milestoneBonuses.length > 0 && (
              <div className="-mt-1 flex flex-col gap-1 bg-white px-4 pt-4">
                {milestoneBonuses.map((bonus, index) => {
                  const title = `${rewardGroupBillingPeriodCalculation(
                    bonus
                  )}-${pluralizeBillingPeriodBeforeFirstReward(
                    bonus
                  )} member bonus!`;
                  const description =
                    index === 0
                      ? (() => {
                          const periodCount =
                            rewardGroupBillingPeriodCalculation(bonus);
                          if (
                            !bonus.hasSelectableRewards &&
                            bonus.membershipRewards.length === 1
                          ) {
                            const item = bonus.membershipRewards[0];
                            const itemName = getProductTitle(
                              item.packageListingTitle,
                              item.packageName
                            );
                            return item.quantity === 1 ? (
                              <>
                                Receive {aOrAn(itemName)} after {periodCount}{" "}
                                {pluralizeBillingPeriodBeforeFirstReward(bonus)}{" "}
                                as {aOrAn(membershipListing.name)} member
                              </>
                            ) : (
                              <>
                                Receive{" "}
                                <strong>
                                  {item.quantity}{" "}
                                  {item.quantity === 1
                                    ? itemName
                                    : `${itemName} treatments`}
                                </strong>{" "}
                                after {periodCount}{" "}
                                {pluralizeBillingPeriodBeforeFirstReward(bonus)}{" "}
                                as {aOrAn(membershipListing.name)} member
                              </>
                            );
                          }
                          return (
                            <>
                              Receive a one-time bonus after {periodCount}{" "}
                              {pluralizeBillingPeriodBeforeFirstReward(bonus)}{" "}
                              as {aOrAn(membershipListing.name)} member
                            </>
                          );
                        })()
                      : undefined;

                  return (
                    <InfoBlock
                      key={bonus.id}
                      icon={index === 0 ? MembershipFilled : undefined}
                      title={title}
                      description={description}
                      callToAction="More details"
                      callToActionClick={() => {
                        setMilestoneModalContent(bonus);
                        setMilestoneModalOpen(true);
                      }}
                    />
                  );
                })}
              </div>
            )}

            <div className="-mt-1 bg-white px-5 pb-2">
              <TermsAndCondition hideText />
            </div>
          </div>

          <div className="z-35 sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4">
            {source ? (
              <div className="flex flex-col gap-3">
                <Button
                  fullWidth={true}
                  onClick={async () => navigate(routeBackNumber)}
                >
                  Back to {source}
                </Button>
                <Button
                  fullWidth={true}
                  style={"secondary"}
                  onClick={async () => navigate("/shop/memberships")}
                >
                  Shop all memberships
                </Button>
              </div>
            ) : (
              <Button
                disabled={
                  !membershipListing.active ||
                  currentMembership != null ||
                  user.isGuest ||
                  addMembershipMutation.isLoading
                }
                fullWidth={true}
                onClick={() => {
                  if (
                    user.platform?.platformSettings
                      ?.zenotiWebStoreMembershipPurchaseRequired
                  ) {
                    if (user.platformLocation?.zenotiId) {
                      window.open(
                        `${user.platform.platformSettings.zenotiWebStoreUrl}/webstoreNew/sales/membership/${user.platformLocation.zenotiId}`,
                        "_blank"
                      );
                    }
                  } else {
                    handleAddToCart();
                  }
                }}
              >
                {getGuestUserMembershipText()}
              </Button>
            )}
          </div>
        </div>
      )}

      {signUpBonus && signUpBonusModalOpen && (
        <ListModal
          open={signUpBonusModalOpen}
          onClose={() => setSignUpBonusModalOpen(false)}
          size="fullscreen"
          title="Sign-up Bonus"
          firstButtonLabel={getGuestUserMembershipText()}
          onFirstButtonClick={handleAddToCart}
          firstButtonDisabled={
            currentMembership != null ||
            user.isGuest ||
            addMembershipMutation.isLoading
          }
        >
          <div className="flex flex-col gap-5 p-6">
            <div className="text-center text-body1 text-primary">
              {buildSignUpBonusModalIntro(signUpBonus, membershipListing)}
            </div>
            <div>
              <ExpandableItems
                items={signUpBonus.membershipRewards.map((item) => {
                  return {
                    id: item.id,
                    title: getProductTitle(
                      item.packageListingTitle,
                      item.packageName
                    ),
                    description: item.packageDescription,
                    subtitle: `${item.quantity} ${pluralize(
                      item.quantity,
                      item.packageUnitType?.singularUnitType ?? "unit",
                      item.packageUnitType?.pluralUnitType ?? "units"
                    )}`,
                  };
                })}
              />
            </div>
          </div>
        </ListModal>
      )}

      {milestoneModalContent?.id && milestoneModalOpen && (
        <ListModal
          open={true}
          onClose={() => setMilestoneModalOpen(false)}
          size="fullscreen"
          title={buildMilestoneBonusModalTitle(
            milestoneModalContent,
            isFlexibleMembershipsEnabled,
            membershipListing.billingPeriodIntervalCount
          )}
          firstButtonLabel={getGuestUserMembershipText()}
          onFirstButtonClick={handleAddToCart}
          firstButtonDisabled={
            currentMembership != null ||
            user.isGuest ||
            addMembershipMutation.isLoading
          }
        >
          <div className="flex flex-col gap-5 p-6">
            <div className="text-center text-body1 text-primary">
              {buildMilestoneBonusModalIntro(
                milestoneModalContent,
                membershipListing
              )}
            </div>
            <div>
              <ExpandableItems
                items={milestoneModalContent.membershipRewards.map((item) => {
                  return {
                    id: item.id,
                    title: getProductTitle(
                      item.packageListingTitle,
                      item.packageName
                    ),
                    description: item.packageDescription,
                    subtitle: `${item.quantity} ${pluralize(
                      item.quantity,
                      item.packageUnitType?.singularUnitType ?? "unit",
                      item.packageUnitType?.pluralUnitType ?? "units"
                    )}`,
                  };
                })}
              />
            </div>
          </div>
        </ListModal>
      )}
    </>
  );
};

export default MembershipPDP;
